import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CategoriesEdit from "../../../../../components/_utils/AccountCreationComponents/CategoriesEdit";
import CommonLayout from "../../../../../components/_utils/CommonLayout";
import SmallPopup from "../../../../../components/_utils/CommonSmallPopup";
import SmallPopupWithBulletPoints from "../../../../../components/_utils/CommonSmallPopupWithBulletPoint";
import PrimaryStepper from "../../../../../components/_utils/CommonSteppers/PrimaryStepper";
import TopNavComponent from "../../../../../components/_utils/CommonTopNav/TopNavComponent";
import {
  beneficiaryAccountDetailsById,
  resetUpdateBeneficiaryAccount,
  updateBeneficiaryAccount,
} from "../../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { getAllCategories } from "../../../../../redux/features/sharedResources/sharedResources";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import "./index.css";
import globalText from "../../../../../utils/globalText.js";

const BeneficiaryAccountEditCategories = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [activeCategories, setActiveCategories] = useState([]);
  const [isUpdateDisabled, setIsUpdateDisabled] = useState(true);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [isLeavePopupOpen, setIsLeavePopupOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showUnsafeCategoryPopup, setShowUnsafeCategoryPopup] = useState(false);
  const [initialSelectedCategories, setInitialSelectedCategories] = useState(
    [],
  );

  const { beneficiaryAccountDetailsData, beneficiaryAccountDetailsLoading } =
    useSelector((state) => state.beneficiaryAccount);

  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );

  const isCategoryLoading =
    beneficiaryAccountDetailsLoading || categories.length === 0;

  const hasUnsavedChanges =
    isDataLoaded &&
    (selectedCategories.length !== initialSelectedCategories.length ||
      selectedCategories.some(
        (category) => !initialSelectedCategories.includes(category),
      ));

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setIsLeavePopupOpen(true);
      return;
    }
    navigate(-1);
  };

  const confirmLeavePage = () => {
    navigate(-1);
  };

  const handleCategoryToggle = (categoryId) => {
    const category = activeCategories.find(
      (category) => category.id === categoryId && category.active === "Y",
    );

    if (!category) return;

    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    );
  };

  const handleToggleSelectAll = () => {
    const activeCategoryIds = activeCategories
      .filter((category) => category.active === "Y")
      .map(({ id }) => id);

    setSelectedCategories(
      selectedCategories.length === activeCategoryIds.length
        ? []
        : activeCategoryIds,
    );
  };

  const handleUpdateCategories = async () => {
    if (isUpdating) return;

    const totalActiveCategories = activeCategories.length;

    const shouldShowUnsafePopup =
      !showUnsafeCategoryPopup &&
      (selectedCategories.length === totalActiveCategories ||
        selectedCategories.length === 0);

    if (shouldShowUnsafePopup) {
      setShowUnsafeCategoryPopup(true);
      return;
    }

    const selectedCategoryIds = selectedCategories.filter((categoryId) =>
      activeCategories.some(
        (category) => category.id === categoryId && category.active === "Y",
      ),
    );

    setIsUpdating(true);
    try {
      await dispatch(
        updateBeneficiaryAccount({
          id,
          category_ids: selectedCategoryIds,
          navigate,
        }),
      ).unwrap();
      dispatch(resetUpdateBeneficiaryAccount());
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/category_edit_success/${id}`,
        {
          state: {
            updatedFields: ["category"],
            updatedCategories: selectedCategoryIds,
          },
          replace: true,
        },
      );
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsUpdating(false);
      setShowUnsafeCategoryPopup(false);
    }
  };

  const dataArray = [
    {
      previousText: "Cancel",
      nextText: "Update",
      previousEnableBgColor: "var(--primary-nav-act)",
      previousDisableBgColor: "var(--primary-nav-act)",
      previousEnableTextColor: "var(--text-primary)",
      previousHoverOption: "white",
      nextHoverOption: "black",
      nextIsEnabled: !isUpdateDisabled,
      previousIsDisabled: isUpdating,
      nextIsLoading: isUpdating,
      nextLoaderNeeded: true,
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor: isUpdating
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: "var(--text-primary)",
      nextDisableTextColor: "var(--text-primary)",
      stepperText: "Category",
    },
  ];

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [, beneficiaryResponse] = await Promise.all([
          dispatch(getAllCategories()),
          dispatch(beneficiaryAccountDetailsById({ id })),
        ]);

        if (
          beneficiaryResponse?.error?.message ===
          "Request failed with status code 404"
        ) {
          navigate("*");
        }
      } catch (error) {
        console.error("Error fetching page data:", error);
      }
    };

    loadInitialData();
  }, [dispatch, id, navigate]);

  useEffect(() => {
    setActiveCategories(
      categories.filter((category) => category.active === "Y"),
    );
  }, [categories]);

  useEffect(() => {
    if (
      beneficiaryAccountDetailsLoading ||
      beneficiaryAccountDetailsData?.status !== true ||
      !categories?.length
    ) {
      return;
    }

    const beneficiaryData =
      beneficiaryAccountDetailsData?.beneficiary_accounts?.[0];

    if (!beneficiaryData) return;

    const initialSelectedCategories =
      beneficiaryData.category_ids
        ?.map((category) => category.category_id)
        .filter((categoryId) =>
          categories.some(
            (category) => category.id === categoryId && category.active === "Y",
          ),
        ) || [];

    setSelectedCategories(initialSelectedCategories);
    setInitialSelectedCategories(initialSelectedCategories);
    setIsDataLoaded(true);
  }, [
    beneficiaryAccountDetailsData,
    beneficiaryAccountDetailsLoading,
    categories,
  ]);

  useEffect(() => {
    if (!isDataLoaded || activeCategories.length === 0) return;

    const hasNoCategoryChanges =
      selectedCategories.length === initialSelectedCategories.length &&
      selectedCategories.every((cat) =>
        initialSelectedCategories.includes(cat),
      );

    setSelectAllChecked(selectedCategories.length === activeCategories.length);
    setIsUpdateDisabled(hasNoCategoryChanges);
  }, [
    selectedCategories,
    activeCategories,
    initialSelectedCategories,
    isDataLoaded,
  ]);

  return (
    <div className="bg-primary-bg flex overflow-y-hidden flex-col h-full-height">
      <div
        className="flex flex-col items-center max-w-tab-width-normal mx-auto my-margin-base w-full bg-primary-nav rounded-b-border-radius-md"
        style={{
          overflowY: showUnsafeCategoryPopup ? "hidden" : "scroll",
          scrollbarWidth: "none",
        }}
      >
        {showUnsafeCategoryPopup && (
          <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
            <SmallPopupWithBulletPoints
              headerTitle={"Unsafe to proceed"}
              bulletPoints={[
                "All categories will be linked to a single account.",
                "It is recommended to distribute categories across appropriate accounts.",
              ]}
              headerFontWeight="semibold"
              headerFontColor="negative-act"
              textSize="font-md"
              btnOneText="Cancel"
              btnOneBtnType="button"
              btnOneEnabledBgColor="transparent"
              btnOneHandleFunction={() => {
                setShowUnsafeCategoryPopup(false);
                setIsUpdating(false);
              }}
              btnOneOutLineColor="var(--text-primary)"
              btnOneEnabledTextColor="var(--text-primary)"
              btnOneIsDisabled={false}
              btnOneHoverOption="white"
              btnTwoText="Proceed"
              btnTwoBtnType="button"
              btnTwoHandleFunction={handleUpdateCategories}
              btnTwoEnabledBgColor="var(--negative-act)"
              btnTwoEnabledTextColor="var(--text-primary)"
              btnTwoIsDisabled={false}
              btnTwoHoverOption="black"
            />
          </div>
        )}
        {isLeavePopupOpen && (
          <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
            <SmallPopup
              headerTitle={globalText?.LEAVE_PAGE_WARNING_HEADER}
              headerFontWeight="semibold"
              headerFontColor="text-primary"
              textSize="font-md"
              subtext={globalText?.EXIT_CURRENT_FLOW_NOTE}
              btnOneText={globalText?.STAY}
              btnOneBtnType="button"
              btnOneEnabledBgColor="transparent"
              btnOneHandleFunction={() => setIsLeavePopupOpen(false)}
              btnOneOutLineColor="var(--text-primary)"
              btnOneEnabledTextColor="var(--text-primary)"
              btnOneIsDisabled={false}
              btnOneHoverOption="white"
              btnTwoText={globalText?.LEAVE}
              btnTwoBtnType="button"
              btnTwoHandleFunction={confirmLeavePage}
              btnTwoEnabledBgColor="var(--negative-act)"
              btnTwoEnabledTextColor="var(--text-primary)"
              btnTwoIsDisabled={false}
              btnTwoHoverOption="black"
            />
          </div>
        )}
        <div className="min-h-navbar-breadcrumb-height hidden Qmd:block"></div>
        <div className="flex justify-center items-end !bottom-bottom-base Qsmd:min-h-[calc(var(--stepper-height)+var(--icon-sm))] min-h-stepper-height Qsmd:bg-transparent rounded-t-border-radius-md Qmd:relative Qlg:bg-transparent bg-primary-nav rounded-b-border-radius-sm w-screen fixed Qsmd:top-top-base left-left-base z-z-index-overlay">
          <PrimaryStepper
            activeStep={GlobalConfiguration?.lengthCheckZero}
            handleForwardAction={handleUpdateCategories}
            handleBackwardAction={handleCancel}
            dataArray={dataArray}
          />
        </div>
        <div className="fixed top-top-base left-left-base mt-margin-base w-width-full rounded-bl-border-radius-md rounded-br-border-radius-md z-z-index-header bg-primary-nav shadow-box-shadow-bottom lg:hidden block">
          <TopNavComponent
            topNavHeader="Beneficiary Account"
            topNavSubText="Category"
            isFormattingNotRequired
          />
        </div>
        <div className="h-mobile-create-header block Qmd:hidden"></div>
      </div>
      <div
        className="max-w-tab-width-normal mx-auto w-full py-padding-md px-padding-md Qlg:px-padding-base flex-1 overflow-y-auto"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="w-width-full">
          <div
            className="max-w-tab-width-normal w-full mx-auto"
            style={{ scrollbarWidth: "none" }}
          >
            <CommonLayout>
              <CategoriesEdit
                sortedCategories={activeCategories}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectAllChecked={selectAllChecked}
                toggleSelectAll={handleToggleSelectAll}
                handleCategorySelect={(category) =>
                  handleCategoryToggle(category.id)
                }
                Title="Categories"
                SubTitle="Not selecting categories will assign all, including future ones, to this account. This is not recommended for security reasons."
                TitleFontcolor="text-primary"
                TextSize="font-md"
                SubTitleFontcolor="text-secondary"
                SubTitleTextSize="font-xs"
                TitleFontWeight="semibold"
                SubTitleFontWeight="normal"
                isCategoryLoading={isCategoryLoading}
                type="CategoryCreate"
              />
            </CommonLayout>
          </div>
        </div>
      </div>
      <div className="h-stepper-moble-height flex-shrink-0 Qsmd:h-pagination-height block Qsmd:hidden"></div>
    </div>
  );
};

export default BeneficiaryAccountEditCategories;
